import { BULK_EDIT_CLEAR } from '../constants';
import type { BulkEditContext } from '../types';

type BulkEditClearParams = {
  value: unknown;
  valueForValidation: unknown;
  valueEmpty: boolean;
  colKey: string;
  required?: boolean;
  onChange: (key: string, value: unknown) => void;
  bulkEditContext?: BulkEditContext;
};

export type BulkEditClearState = {
  isBulkEdit: boolean;
  isCleared: boolean;
  /**
   * Show "Set empty" button below input (text/number/description/date).
   *
   * Stays visible as long as the items have a pre-existing value to clear,
   * even if the user has typed something — clicking it replaces the typed
   * value with BULK_EDIT_CLEAR.
   */
  showClearButton: boolean;
  /**
   * Show "Set empty" option inside the dropdown menu (single + multiselect).
   *
   * Stays visible as long as the items have a pre-existing value to clear,
   * regardless of what the user has selected so far. Treat it like any other
   * menu option — the user can keep picking, then later decide to set empty
   * without having to first deselect everything.
   */
  showClearOption: boolean;
  handleBulkEditChange: (newValue: string) => string;
  handleClear: () => void;
  handleUndoClear: () => void;
};

/**
 * Derives bulk-edit clear state and callbacks shared by text, description,
 * and number field renderers.
 *
 * This is a plain function (not a hook) because the field renderers are
 * not React function components — they're render helpers called during render.
 */
export function getBulkEditClearState({
  valueForValidation,
  valueEmpty,
  colKey,
  required,
  onChange,
  bulkEditContext,
}: BulkEditClearParams): BulkEditClearState {
  const isBulkEdit = bulkEditContext?.isBulkEdit ?? false;
  const isRequired = required ?? false;
  const isCleared = valueForValidation === BULK_EDIT_CLEAR;
  const hasExistingValue = bulkEditContext?.hasExistingValue ?? false;
  const hideSetEmpty = bulkEditContext?.hideSetEmpty ?? true;
  // Both the text/number/date "Set empty" BUTTON and the dropdown OPTION stay
  // visible as long as the items have a pre-existing value to clear. The user
  // can switch to "empty" at any point during their edit without first having
  // to delete what they typed or deselect what they picked — clicking it just
  // replaces the in-progress value with BULK_EDIT_CLEAR.
  const showClearButton =
    isBulkEdit && !hideSetEmpty && !isRequired && !isCleared && hasExistingValue && valueEmpty;
  const showClearOption = isBulkEdit && !hideSetEmpty && !isRequired && hasExistingValue;

  const handleBulkEditChange = (newValue: string) => {
    return newValue;
  };

  const handleClear = () => {
    onChange(colKey, BULK_EDIT_CLEAR);
  };

  const handleUndoClear = () => {
    bulkEditContext?.onKeepCurrent();
  };

  return {
    isBulkEdit,
    isCleared,
    showClearButton,
    showClearOption,
    handleBulkEditChange,
    handleClear,
    handleUndoClear,
  };
}
